import { useState, useEffect } from "react";
import { api, createPollingSubscription } from "@/lib/apiClient";
import logger from '@/lib/logger';

export interface OperatorAssignment {
  id: string;
  full_name: string;
  area: string | null;
  employee_id: string | null;
  shift: string | null;
  isActive: boolean;
  currentProcess?: string;
  workOrderNumber?: string;
}

export interface AreaDistribution {
  area: string;
  total: number;
  active: number;
  inactive: number;
  percentage: number;
}

export const useOperatorAssignment = () => {
  const [operators, setOperators] = useState<OperatorAssignment[]>([]);
  const [distribution, setDistribution] = useState<AreaDistribution[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOperatorData = async () => {
    try {
      // Get all operators with their profiles
      const { data: profiles, error: profilesError } = await api
        .from("profiles")
        .select("id, full_name, area, employee_id, shift");

      if (profilesError) throw profilesError;

      // Get active processes for today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: activeProcesses, error: processError } = await api
        .from("process_records")
        .select(`
          operator_id,
          manufacturing_processes(name),
          work_orders(order_number)
        `)
        .in("status", ["in_progress", "paused"])
        .gte("started_at", today.toISOString());

      if (processError) throw processError;

      // Map operators with their active status
      const operatorsWithStatus: OperatorAssignment[] = profiles?.map((profile) => {
        const activeProcess = activeProcesses?.find(
          (p) => p.operator_id === profile.id
        );

        return {
          id: profile.id,
          full_name: profile.full_name,
          area: profile.area,
          employee_id: profile.employee_id,
          shift: profile.shift,
          isActive: !!activeProcess,
          currentProcess: activeProcess?.manufacturing_processes?.name,
          workOrderNumber: activeProcess?.work_orders?.order_number,
        };
      }) || [];

      setOperators(operatorsWithStatus);

      // Calculate distribution by area
      const areas = ["corte_plotter", "laminador", "taller", "generico"];
      const areaStats: AreaDistribution[] = areas.map((area) => {
        const areaOperators = operatorsWithStatus.filter((op) => op.area === area);
        const active = areaOperators.filter((op) => op.isActive).length;
        const total = areaOperators.length;
        
        return {
          area,
          total,
          active,
          inactive: total - active,
          percentage: operatorsWithStatus.length > 0 
            ? Math.round((total / operatorsWithStatus.length) * 100)
            : 0,
        };
      });

      setDistribution(areaStats);
      setLoading(false);
    } catch (error) {
      logger.error("Error fetching operator assignment:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperatorData();

    // Set up polling subscription (cada 30 segundos)
    const pollingSubscription = createPollingSubscription(
      fetchOperatorData,
      30000
    );

    return () => {
      pollingSubscription.unsubscribe();
    };
  }, []);

  return { operators, distribution, loading, refresh: fetchOperatorData };
};